import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FiBarChart2, FiSmartphone } from 'react-icons/fi';
import { Button, Card } from '@/components';
import { apiJson } from '@/lib/api';
import type { UserResponse } from '@/lib/types';
import { track } from '@/lib/analytics';

function PortalChoicePage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const userQuery = useQuery({
    queryKey: ['portal-choice-user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  useEffect(() => {
    track('Portal Choice Screen');
  }, []);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/auth/sign-in' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userQuery.data) return;
    const roles = userQuery.data.roles ?? [];
    if (roles.includes('staff')) return;
    if (roles.includes('coparent')) {
      navigate({ to: '/app/expenses' });
    } else if (roles.includes('professional')) {
      navigate({ to: '/pro' });
    } else if (roles.includes('divorce_customer')) {
      navigate({ to: '/divorce' });
    }
  }, [isLoaded, isSignedIn, userQuery.data, navigate]);

  const goToUserApp = () => {
    track('Portal Choice User App');
    navigate({ to: '/app/expenses' });
  };

  const goToAdminPortal = () => {
    track('Portal Choice Admin Portal');
    navigate({ to: '/admin' });
  };

  if (!isLoaded || !isSignedIn || userQuery.isLoading) {
    return null;
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-md mx-auto px-6 py-8'>
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Where would you like to go?
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Choose the co-parent app or the admin dashboard.
          </p>
        </div>

        <div className='space-y-4'>
          <Card className='p-0 overflow-hidden'>
            <button
              type='button'
              onClick={goToUserApp}
              className='w-full text-left p-5 hover:bg-gray-50 transition-colors'
            >
              <div className='flex items-start gap-4'>
                <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                  <FiSmartphone size={22} />
                </div>
                <div className='min-w-0'>
                  <h2 className='text-lg font-semibold text-gray-900'>
                    User experience
                  </h2>
                  <p className='mt-1 text-sm text-gray-500'>
                    Use Harbor as a co-parent: expenses, calendar, journal, and
                    more.
                  </p>
                </div>
              </div>
            </button>
          </Card>

          <Card className='p-0 overflow-hidden'>
            <button
              type='button'
              onClick={goToAdminPortal}
              className='w-full text-left p-5 hover:bg-gray-50 transition-colors'
            >
              <div className='flex items-start gap-4'>
                <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-900/10 text-gray-900'>
                  <FiBarChart2 size={22} />
                </div>
                <div className='min-w-0'>
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Admin portal
                  </h2>
                  <p className='mt-1 text-sm text-gray-500'>
                    Manage users, metrics, outreach, and internal tools.
                  </p>
                </div>
              </div>
            </button>
          </Card>
        </div>

        <div className='mt-6'>
          <Button variant='secondary' fullWidth onClick={goToUserApp}>
            Continue to co-parent app
          </Button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/auth/portal-choice')({
  component: PortalChoicePage,
});
